import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { Change, Client, Project, User } from '../../models';
import {
    loadChange,
    loadClients,
    loadProjects,
    loadUsers,
    toChangeStep,
    useAppDispatch,
    useAppSelector,
    RootState,
} from '../../redux';
import { Changes } from '../../services';
import {
    Ability, Alert, Button, ChangeListItem, ChangeModal, Confirm, Icon, Loader,
    MergeChangesModal, QuickChangeModal, RequesterEmails,
} from '../../components';
import TriageModifyModal from '../Triage/TriageModifyModal';
import {
    decodeRequesterEmailParam,
    requesterDomain,
} from '../../utils/requesterPath';
import { requesterEmailsOf } from '../../utils/changeRequester';

const CLOSED_STATUSES = [
    Change.STATUS_FINISHED,
    Change.STATUS_DEPLOYED,
    Change.STATUS_MERGED,
].join(',');

function canBulkAccept(change: Change): boolean {
    return change.progress?.status === Change.STATUS_PENDING_TRIAGE;
}

function RequesterView() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { email: emailParam } = useParams<{ email: string }>();
    const email = decodeRequesterEmailParam(emailParam);

    const { clients } = useAppSelector((state: RootState) => state.clients);
    const { change: detailChange } = useAppSelector((state: RootState) => state.changes);
    const { projects } = useAppSelector((state: RootState) => state.projects);
    const { users } = useAppSelector((state: RootState) => state.users);
    const { user } = useAppSelector((state: RootState) => state.auth);

    const [openChanges, setOpenChanges] = useState<Change[]>([]);
    const [closedChanges, setClosedChanges] = useState<Change[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [modalChangeId, setModalChangeId] = useState<string | undefined>();
    const [quickCreateOpen, setQuickCreateOpen] = useState(false);
    const [selectedChangeIds, setSelectedChangeIds] = useState<string[]>([]);
    const [processingBulk, setProcessingBulk] = useState(false);
    const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
    const [modifyModalOpen, setModifyModalOpen] = useState(false);
    const [modifyTargetIds, setModifyTargetIds] = useState<string[]>([]);
    const [mergeModalOpen, setMergeModalOpen] = useState(false);
    const [mergeTargetIds, setMergeTargetIds] = useState<string[]>([]);
    const [bulkFeedback, setBulkFeedback] = useState<{
        title: string;
        color: 'success' | 'failure' | 'warning';
    } | null>(null);

    const domain = useMemo(() => requesterDomain(email), [email]);

    const matchedClient = useMemo(() => {
        if (!email || !clients?.length)
            return null;
        const needle = email.toLowerCase();
        return (clients as Client[]).find(
            (client) => String(client.email || '').toLowerCase() === needle
        ) || null;
    }, [clients, email]);

    const relatedEmails = useMemo(() => {
        const emails = new Set<string>();
        if (email)
            emails.add(email);
        [...openChanges, ...closedChanges].forEach((change) => {
            requesterEmailsOf(change).forEach((item) => emails.add(item));
        });
        return Array.from(emails).filter((item) => item.toLowerCase() !== email.toLowerCase());
    }, [email, openChanges, closedChanges]);

    const allChanges = useMemo(
        () => [...openChanges, ...closedChanges],
        [openChanges, closedChanges],
    );

    const selectedChanges = useMemo(
        () => allChanges.filter((item) => selectedChangeIds.includes(item._id)),
        [allChanges, selectedChangeIds],
    );

    const acceptableSelected = useMemo(
        () => selectedChanges.filter(canBulkAccept),
        [selectedChanges],
    );

    const selectedCount = selectedChangeIds.length;
    const allSelected = allChanges.length > 0 && selectedCount === allChanges.length;
    const someSelected = selectedCount > 0 && !allSelected;

    const bulkDeleteConfirmTitle = selectedCount > 1
        ? t('changes_overview.confirm_delete_many', { count: selectedCount })
        : t('changes_overview.confirm_delete_one');

    const selectableProjects = useMemo(() => {
        return (projects || []).slice().sort((a: Project, b: Project) => a.name.localeCompare(b.name));
    }, [projects]);

    const selectableAssignees = useMemo(() => {
        const byId: Record<string, User> = {};
        (users || []).forEach((item: User) => { byId[item._id] = item; });
        return Object.values(byId)
            .filter((item: User) => item.role !== User.ROLE_CLIENT)
            .sort((a, b) => a.fullname().localeCompare(b.fullname()));
    }, [users]);

    const reload = useCallback(async () => {
        if (!email) {
            setOpenChanges([]);
            setClosedChanges([]);
            return;
        }
        setLoading(true);
        setError('');
        try {
            const [openResult, closedResult] = await Promise.all([
                Changes.list({ requestor_email: email, notfinished: true }),
                Changes.list({
                    requestor_email: email,
                    'progress.status': CLOSED_STATUSES,
                }),
            ]);
            setOpenChanges(openResult.changes || []);
            setClosedChanges(closedResult.changes || []);
        } catch (err: any) {
            setError(err?.message || t('requester.load_error') || '');
            setOpenChanges([]);
            setClosedChanges([]);
        } finally {
            setLoading(false);
        }
    }, [email, t]);

    useEffect(() => {
        dispatch(loadClients());
        dispatch(loadProjects());
        dispatch(loadUsers());
    }, [dispatch]);

    useEffect(() => {
        reload();
    }, [reload]);

    useEffect(() => {
        setSelectedChangeIds([]);
        setBulkFeedback(null);
    }, [email]);

    useEffect(() => {
        if (modalChangeId)
            dispatch(loadChange(modalChangeId));
    }, [dispatch, modalChangeId]);

    useEffect(() => {
        const base = t('requester.title') || 'Requester';
        document.title = email ? `${email} · ${base}` : base;
        return () => { document.title = 'Yoda'; };
    }, [email, t]);

    const openChangeModal = (change: Change) => setModalChangeId(change._id);
    const closeChangeModal = () => setModalChangeId(undefined);
    const onRemoveCard = (_removed: AnyObj) => {
        // Soft-delete is handled inside ChangeModal; refresh the list.
        closeChangeModal();
        reload();
    };

    const toggleChangeSelection = (changeId: string) => {
        setSelectedChangeIds((prev) => (
            prev.includes(changeId) ? prev.filter((id) => id !== changeId) : [...prev, changeId]
        ));
    };

    const toggleSelectAll = () => {
        if (allSelected) {
            setSelectedChangeIds([]);
            return;
        }
        setSelectedChangeIds(allChanges.map((item) => item._id));
    };

    const reportBulkResult = (successCount: number, errorCount: number, requestedCount: number) => {
        if (errorCount === 0) {
            setBulkFeedback({
                color: 'success',
                title: t('priorities.bulk_success', { count: successCount }) as string,
            });
            return;
        }
        if (successCount > 0) {
            setBulkFeedback({
                color: 'warning',
                title: t('priorities.bulk_partial', {
                    success: successCount,
                    failed: errorCount,
                }) as string,
            });
            return;
        }
        setBulkFeedback({
            color: 'failure',
            title: t('priorities.bulk_error', { count: requestedCount }) as string,
        });
    };

    const openModifyModal = (ids: string[]) => {
        if (!ids.length) return;
        setModifyTargetIds(ids);
        setModifyModalOpen(true);
    };

    const openMergeModal = (ids: string[]) => {
        if (ids.length < 2) return;
        setMergeTargetIds(ids);
        setMergeModalOpen(true);
    };

    const applyModify = async (payload: { assigneeId?: string; projectId?: string }) => {
        const requestedCount = modifyTargetIds.length;
        setBulkFeedback(null);
        setProcessingBulk(true);
        try {
            const result = await Changes.bulkFromTriage({
                action: 'modify',
                changeIds: modifyTargetIds,
                assignee: payload.assigneeId,
                projectId: payload.projectId,
            });
            const errorCount = result.errors?.length || 0;
            reportBulkResult(result.changes.length, errorCount, requestedCount);
            setModifyModalOpen(false);
            setModifyTargetIds([]);
            setSelectedChangeIds([]);
            reload();
        } catch {
            reportBulkResult(0, requestedCount, requestedCount);
        } finally {
            setProcessingBulk(false);
        }
    };

    const applyMerge = async (target: Change) => {
        if (!target?._id || !mergeTargetIds.length) return;
        const requestedCount = mergeTargetIds.length;
        setBulkFeedback(null);
        setProcessingBulk(true);
        try {
            await Changes.merge(target._id, mergeTargetIds);
            reportBulkResult(requestedCount, 0, requestedCount);
            setMergeModalOpen(false);
            setMergeTargetIds([]);
            setSelectedChangeIds([]);
            if (modalChangeId && mergeTargetIds.includes(modalChangeId)) {
                closeChangeModal();
            }
            reload();
        } catch {
            reportBulkResult(0, requestedCount, requestedCount);
        } finally {
            setProcessingBulk(false);
        }
    };

    const resolveCards = async (ids: string[]) => {
        if (!ids.length) return;
        const requestedCount = ids.length;
        setBulkFeedback(null);
        setProcessingBulk(true);
        try {
            const result = await Changes.bulkFromTriage({
                action: 'resolve',
                changeIds: ids,
                assignee: user?._id,
            });
            const errorCount = result.errors?.length || 0;
            reportBulkResult(result.changes.length, errorCount, requestedCount);
            setSelectedChangeIds([]);
            reload();
        } catch {
            reportBulkResult(0, requestedCount, requestedCount);
        } finally {
            setProcessingBulk(false);
        }
    };

    const discardCards = async (ids: string[]) => {
        if (!ids.length) return;
        const requestedCount = ids.length;
        setBulkFeedback(null);
        setProcessingBulk(true);
        try {
            const result = await Changes.bulkFromTriage({
                action: 'discard',
                changeIds: ids,
            });
            const errorCount = result.errors?.length || 0;
            reportBulkResult(result.changes.length, errorCount, requestedCount);
            setSelectedChangeIds([]);
            setShowBulkDeleteConfirm(false);
            reload();
        } catch {
            reportBulkResult(0, requestedCount, requestedCount);
            setShowBulkDeleteConfirm(false);
        } finally {
            setProcessingBulk(false);
        }
    };

    const performBulkAccept = async () => {
        if (!acceptableSelected.length) return;
        setProcessingBulk(true);
        setBulkFeedback(null);
        for (const change of acceptableSelected) {
            await new Promise<void>((resolve) => {
                dispatch(toChangeStep(change._id, Change.STATUS_PENDING_ESTIMATE, {}, () => resolve()));
            });
        }
        setSelectedChangeIds([]);
        setProcessingBulk(false);
        setBulkFeedback({
            color: 'success',
            title: t('priorities.bulk_success', { count: acceptableSelected.length }) as string,
        });
        reload();
    };

    const renderSection = (title: string, items: Change[], emptyKey: string) => (
        <section className="mt-5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {title}
                <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                    ({items.length})
                </span>
            </h2>
            {!items.length ? (
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{t(emptyKey)}</p>
            ) : (
                <ul className="mt-3 divide-y divide-gray-100">
                    {items.map((change) => (
                        <li key={change._id} className="py-2 first:pt-0 last:pb-0">
                            <ChangeListItem
                                change={change}
                                showProject
                                selectable
                                selected={selectedChangeIds.includes(change._id)}
                                onToggleSelect={(item) => toggleChangeSelection(item._id)}
                                onClick={() => openChangeModal(change)}
                            />
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );

    const hasMeta = !!(matchedClient || domain || relatedEmails.length);

    return (
        <Ability can="change:read" redirect="/">
            <div className="RequesterView">
                <header className="border-b bg-white dark:bg-gray-800 px-4 py-3 sm:px-6 lg:px-8">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                            <h2 className="flex items-center text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                                <Icon type="email" size={7} className="mr-1.5" />
                                {t('requester.title')}
                            </h2>
                            {email ? (
                                <p className="mt-1 break-all text-sm text-gray-500 dark:text-gray-400">{email}</p>
                            ) : (
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('requester.missing_email')}</p>
                            )}
                        </div>
                        {email && (
                            <Button
                                color="primary"
                                icon="plus"
                                title={t('requester.new_change') || ''}
                                onClick={() => setQuickCreateOpen(true)}
                            />
                        )}
                    </div>
                </header>

                <main className="m-5">
                    {!email ? (
                        <Alert title={t('requester.missing_email') || ''} icon="warning" color="warning" />
                    ) : (
                        <>
                            {hasMeta && (
                                <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
                                    <div className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
                                        {matchedClient && (
                                            <p>
                                                {t('requester.client')}:{' '}
                                                <Link
                                                    to={`/clients/${matchedClient._id}`}
                                                    className="font-medium text-blue-700 hover:underline"
                                                >
                                                    {matchedClient.name()}
                                                </Link>
                                            </p>
                                        )}
                                        {domain && (
                                            <p>
                                                {t('requester.domain')}:{' '}
                                                <span className="font-medium text-gray-800 dark:text-gray-100">{domain}</span>
                                            </p>
                                        )}
                                        {relatedEmails.length > 0 && (
                                            <div className="flex flex-wrap items-baseline gap-1">
                                                <span>{t('requester.related_emails')}:</span>
                                                <RequesterEmails
                                                    emails={relatedEmails}
                                                    className="text-sm text-gray-700 dark:text-gray-200"
                                                    stopPropagation={false}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </section>
                            )}

                            {error && (
                                <div className="mt-5">
                                    <Alert title={error} icon="warning" color="failure" />
                                </div>
                            )}

                            {!loading && selectedCount > 0 && (
                                <div className="mt-5 rounded-lg border bg-white dark:bg-gray-800 p-3 shadow-sm">
                                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                        <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold select-none">
                                            <input
                                                type="checkbox"
                                                checked={allSelected}
                                                ref={(el) => { if (el) el.indeterminate = someSelected; }}
                                                onChange={toggleSelectAll}
                                                className="h-4 w-4 cursor-pointer"
                                            />
                                            {t('priorities.bulk_actions')}
                                            <span className="ml-1 text-blue-600">({selectedCount})</span>
                                            {processingBulk && (
                                                <span className="ml-2 inline-flex items-center gap-1 font-normal text-gray-500 dark:text-gray-400">
                                                    <Icon type="loading" size={4} color="gray-500" />
                                                    {t('priorities.bulk_processing')}
                                                </span>
                                            )}
                                        </label>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <Button
                                                title={t('priorities.modify') as string}
                                                icon="edit"
                                                small
                                                color="primary"
                                                onClick={() => { if (!processingBulk) openModifyModal(selectedChangeIds); }}
                                                className={processingBulk ? 'opacity-50' : ''}
                                            />
                                            <Button
                                                title={t('priorities.bulk_merge') as string}
                                                icon="merge"
                                                small
                                                color="primary"
                                                onClick={() => { if (!processingBulk) openMergeModal(selectedChangeIds); }}
                                                className={processingBulk || selectedCount < 2 ? 'opacity-50' : ''}
                                            />
                                            <Button
                                                title={t('priorities.resolve') as string}
                                                icon="check"
                                                small
                                                color="success"
                                                onClick={() => { if (!processingBulk) resolveCards(selectedChangeIds); }}
                                                className={processingBulk ? 'opacity-50' : ''}
                                            />
                                            <Button
                                                title={t('priorities.discard') as string}
                                                icon="delete"
                                                small
                                                color="danger"
                                                onClick={() => {
                                                    if (!processingBulk && selectedCount) {
                                                        setShowBulkDeleteConfirm(true);
                                                    }
                                                }}
                                                className={processingBulk ? 'opacity-50' : ''}
                                            />
                                            {acceptableSelected.length > 0 && (
                                                <Button
                                                    title={`${t('changes_overview.bulk.accept')} (${acceptableSelected.length})`}
                                                    icon="check"
                                                    small
                                                    color="success"
                                                    onClick={() => {
                                                        if (!processingBulk) performBulkAccept();
                                                    }}
                                                    className={processingBulk ? 'opacity-50' : ''}
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <Confirm
                                        visible={showBulkDeleteConfirm}
                                        onConfirm={() => discardCards(selectedChangeIds)}
                                        onCancel={() => setShowBulkDeleteConfirm(false)}
                                        title={bulkDeleteConfirmTitle || ''}
                                    />
                                </div>
                            )}

                            {bulkFeedback && (
                                <div className="mt-3">
                                    <Alert
                                        title={bulkFeedback.title}
                                        color={bulkFeedback.color}
                                        icon={bulkFeedback.color === 'success' ? 'check' : 'warning'}
                                    />
                                </div>
                            )}

                            {loading ? (
                                <div className="mt-8 flex justify-center">
                                    <Loader />
                                </div>
                            ) : (
                                <>
                                    {renderSection(
                                        t('requester.open_changes') || '',
                                        openChanges,
                                        'requester.empty_open'
                                    )}
                                    {renderSection(
                                        t('requester.closed_changes') || '',
                                        closedChanges,
                                        'requester.empty_closed'
                                    )}
                                </>
                            )}
                        </>
                    )}
                </main>

                {detailChange && detailChange._id === modalChangeId && detailChange.project?.name && (
                    <ChangeModal
                        project={detailChange.project}
                        change={detailChange}
                        show={true}
                        showProjectName={true}
                        onClose={closeChangeModal}
                        onRemove={onRemoveCard}
                    />
                )}

                {email && (
                    <QuickChangeModal
                        show={quickCreateOpen}
                        initialRequesterEmails={[email]}
                        initialMode="support"
                        onClose={() => setQuickCreateOpen(false)}
                        onAdded={() => {
                            setQuickCreateOpen(false);
                            reload();
                        }}
                    />
                )}

                <TriageModifyModal
                    show={modifyModalOpen}
                    count={modifyTargetIds.length}
                    assignees={selectableAssignees}
                    projects={selectableProjects}
                    processing={processingBulk}
                    onClose={() => {
                        setModifyModalOpen(false);
                        setModifyTargetIds([]);
                    }}
                    onApply={applyModify}
                />

                <MergeChangesModal
                    show={mergeModalOpen}
                    sources={allChanges.filter((item) => mergeTargetIds.includes(item._id))}
                    processing={processingBulk}
                    defaultProjectId={allChanges.find((item) => mergeTargetIds.includes(item._id))?.project?._id}
                    onClose={() => {
                        setMergeModalOpen(false);
                        setMergeTargetIds([]);
                    }}
                    onApply={applyMerge}
                />
            </div>
        </Ability>
    );
}

export default RequesterView;
